import { Button } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useTranslation from '../../localization/useTranslation';
import { brand, shell } from '../../theme/palette';
import { isAuthenticated } from '../../utilities/authentication';
import {
  acceptConsent,
  needsConsentPrompt,
  subscribeConsentChange,
} from '../../utilities/cookieConsent';
import { initConsentScripts } from '../../utilities/consentScripts';

/**
 * Simple cookie banner (Accept only). Hidden on login and other unauthenticated pages.
 */
const CookieConsent = () => {
  const { t } = useTranslation();
  const { language } = useSelector((state) => state.language);
  const { theme } = useSelector((state) => state.theme);
  const [visible, setVisible] = useState(false);
  const isRtl = language === 'ar';
  const colors = theme === 'dark' ? shell.dark : shell.light;

  const refresh = useCallback(() => {
    setVisible(isAuthenticated() && needsConsentPrompt());
  }, []);

  useEffect(() => {
    initConsentScripts();
    refresh();
    return subscribeConsentChange(() => {
      refresh();
    });
  }, [refresh]);

  const handleAccept = () => {
    acceptConsent();
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.title')}
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 1100,
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <div
        style={{
          margin: '0 auto',
          maxWidth: 720,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 16,
          padding: '14px 18px',
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.chromeBg,
          color: colors.text,
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
        }}
      >
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('cookies.title')}</div>
          <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>
            {t('cookies.message')}
          </div>
        </div>
        <Button
          type="primary"
          onClick={handleAccept}
          style={{ backgroundColor: brand.primary, borderColor: brand.primary }}
        >
          {t('cookies.accept')}
        </Button>
      </div>
    </div>
  );
};

export default CookieConsent;
